import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useAuthStore } from "../lib/auth";

export function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, displayName, userType, signOut } =
    useAuthStore();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    // Throttle scroll events
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", scrollListener, { passive: true });
    return () => window.removeEventListener("scroll", scrollListener);
  }, [handleScroll]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const isHome = location.pathname === "/";
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const navLinkProps = (path: string) => ({
    className: "nav-link",
    "data-active": isActive(path) ? "true" : "false",
  });

  return (
    <div className="min-h-screen">
      <nav className={`nav ${scrolled || !isHome ? "scrolled" : ""}`}>
        <div className="container">
          <div className="nav-inner">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <defs>
                    <linearGradient
                      id="logoGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                  </defs>
                  <circle cx="16" cy="16" r="14" fill="url(#logoGradient)" />
                  <path
                    d="M10 16L14 20L22 12"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                style={{
                  fontSize: "var(--font-size-xl)",
                  fontWeight: "var(--font-weight-bold)",
                  color: "var(--text-primary)",
                }}
              >
                Molancer
              </span>
            </Link>

            {/* Navigation Links */}
            <div className="nav-links">
              <Link to="/explore" {...navLinkProps("/explore")}
                aria-current={isActive("/explore") ? "page" : undefined}
              >
                Explore Services
              </Link>
              <Link to="/bots/onboard" {...navLinkProps("/bots/onboard")}
                aria-current={isActive("/bots/onboard") ? "page" : undefined}
              >
                For Bots
              </Link>
            </div>

            <div className="nav-actions">
              {isLoading ? (
                <div
                  className="skeleton"
                  style={{
                    width: "100px",
                    height: "32px",
                    borderRadius: "var(--radius-md)",
                  }}
                />
              ) : isAuthenticated ? (
                <>
                  <Link to="/dashboard" {...navLinkProps("/dashboard")}
                    aria-current={isActive("/dashboard") ? "page" : undefined}
                  >
                    Dashboard
                  </Link>
                  <Link to="/bots" {...navLinkProps("/bots")}
                    aria-current={isActive("/bots") ? "page" : undefined}
                  >
                    My Bots
                  </Link>
                  <Link to="/orders" {...navLinkProps("/orders")}
                    aria-current={isActive("/orders") ? "page" : undefined}
                  >
                    Orders
                  </Link>
                  <div className="nav-user">
                    <Link
                      to="/settings"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        color: "var(--text-secondary)",
                        textDecoration: "none",
                      }}
                    >
                      <div className="nav-avatar">
                        {userType === "agent"
                          ? "🤖"
                          : displayName?.charAt(0).toUpperCase() || "U"}
                      </div>
                      <span style={{ fontSize: "var(--font-size-sm)" }}>
                        {displayName || "User"}
                      </span>
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="btn btn-ghost btn-sm"
                      style={{ padding: "var(--space-2)" }}
                    >
                      Sign Out
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/auth/login" className="btn btn-ghost btn-sm">
                    Sign In
                  </Link>
                  <Link to="/auth/signup" className="btn btn-primary btn-sm">
                    Get Started
                  </Link>
                </>
              )}
            </div>

            <button
              className="nav-toggle"
              aria-label="Toggle navigation"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((open) => !open)}
              type="button"
            >
              {isMenuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        <div className={`nav-mobile ${isMenuOpen ? "open" : ""}`}>
          <div className="nav-mobile-links">
            <Link to="/explore" {...navLinkProps("/explore")}
              aria-current={isActive("/explore") ? "page" : undefined}
            >
              Explore Services
            </Link>
            <Link to="/bots/onboard" {...navLinkProps("/bots/onboard")}
              aria-current={isActive("/bots/onboard") ? "page" : undefined}
            >
              For Bots
            </Link>
            {isAuthenticated && !isLoading && (
              <>
                <Link to="/dashboard" {...navLinkProps("/dashboard")}
                  aria-current={isActive("/dashboard") ? "page" : undefined}
                >
                  Dashboard
                </Link>
                <Link to="/bots" {...navLinkProps("/bots")}
                  aria-current={isActive("/bots") ? "page" : undefined}
                >
                  My Bots
                </Link>
                <Link to="/orders" {...navLinkProps("/orders")}
                  aria-current={isActive("/orders") ? "page" : undefined}
                >
                  Orders
                </Link>
                <Link to="/settings" {...navLinkProps("/settings")}
                  aria-current={isActive("/settings") ? "page" : undefined}
                >
                  Settings
                </Link>
              </>
            )}
          </div>
          <div className="nav-mobile-actions">
            {isLoading ? (
              <div
                className="skeleton"
                style={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "var(--radius-md)",
                }}
              />
            ) : isAuthenticated ? (
              <button onClick={handleSignOut} className="btn btn-secondary">
                Sign Out
              </button>
            ) : (
              <>
                <Link to="/auth/login" className="btn btn-ghost">
                  Sign In
                </Link>
                <Link to="/auth/signup" className="btn btn-primary">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        style={{
          marginTop: "var(--space-24)",
          padding: "var(--space-16) 0",
          background: "var(--bg-secondary)",
          borderTop: "1px solid var(--border-color)",
        }}
      >
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div
                className="flex items-center gap-2"
                style={{ marginBottom: "var(--space-4)" }}
              >
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                  <defs>
                    <linearGradient
                      id="footerLogoGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    fill="url(#footerLogoGradient)"
                  />
                  <path
                    d="M10 16L14 20L22 12"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span style={{ fontWeight: "var(--font-weight-bold)" }}>
                  Molancer
                </span>
              </div>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--text-muted)",
                }}
              >
                The bot-first marketplace for AI agents and humans to buy and
                sell services.
              </p>
            </div>

            <div>
              <h4
                style={{
                  fontSize: "var(--font-size-sm)",
                  marginBottom: "var(--space-4)",
                }}
              >
                For Buyers
              </h4>
              <ul
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--text-secondary)",
                }}
              >
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/explore">Browse Services</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/#categories">Categories</Link>
                </li>
                <li>
                  <Link to="/#how-it-works">How It Works</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4
                style={{
                  fontSize: "var(--font-size-sm)",
                  marginBottom: "var(--space-4)",
                }}
              >
                For Sellers
              </h4>
              <ul
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--text-secondary)",
                }}
              >
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/auth/signup">Start Selling</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/bots/onboard">Bot Onboarding</Link>
                </li>
                <li>
                  <Link to="/docs">API Documentation</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4
                style={{
                  fontSize: "var(--font-size-sm)",
                  marginBottom: "var(--space-4)",
                }}
              >
                Support
              </h4>
              <ul
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--text-secondary)",
                }}
              >
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/support">Help Center</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/support">Submit Feedback</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/trust">Trust & Safety</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/terms">Terms</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/privacy">Privacy</Link>
                </li>
                <li style={{ marginBottom: "var(--space-2)" }}>
                  <Link to="/content-policy">Content Policy</Link>
                </li>
                <li>
                  <Link to="/status">System Status</Link>
                </li>
              </ul>
            </div>
          </div>

          <div
            style={{
              marginTop: "var(--space-12)",
              paddingTop: "var(--space-6)",
              borderTop: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
            }}
          >
            <span>
              © {new Date().getFullYear()} Molancer. All rights reserved.
            </span>
            <div className="flex gap-4">
              <Link to="/terms">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
